import { Checkbox, CheckboxGroup, Flex, Input, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { useAppDispatch } from 'modules/Editor/store';
import { updateForm } from 'modules/Editor/store/interactionSlice';
import { ChangeEvent, MouseEvent, forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetState } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { useWidget } from 'widgets/sdk';

import { DEFUALT_SELECTION_FIELD_LAYOUT } from '../FormWidget.config';
import { SELECTION_FIELD_INPUT_TEST_ID } from '../FormWidget.config';
import {
  FormType,
  FormWidgetData,
  OtherInputProps,
  SelectionFieldLayoutType,
  SelectionFieldProps,
} from '../FormWidget.types';
import { InputBase } from '../InputBase';

export const SelectionField = forwardRef<HTMLLabelElement, SelectionFieldProps>(
  (
    {
      disableTabbability,
      storedValue = '',
      isReadOnly = false,
      isLabelFocused = false,
      setWidgetState = (_: WidgetState) => {},
    },
    ref,
  ) => {
    const {
      widgetId,
      style,
      field: formField,
      labelPosition,
      isWidgetSelected,
      selectionFieldData,
    } = useWidget<FormWidgetData>();
    const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });
    const {
      gap,
      color,
      fontFamily,
      fontSize,
      fontWeight,
      fontStyle,
      lineHeight,
      textAlign,
      textDecoration,
      backgroundColor,
      borderColor,
      borderRadius,
      borderWidth,
    } = style;

    const [value, setValue] = useState<string[] | undefined>(storedValue ? storedValue.split(',') : undefined);

    // if storedValue existing and the prefix has the other toggle index, set the other value
    const [otherValue, setOtherValue] = useState<string>(
      storedValue
        ?.split(',')
        ?.find((v) => v.startsWith(`${selectionFieldData?.options.length}-`))
        ?.split('-')[1] ?? '',
    );
    const dispatch = useAppDispatch();
    const selectionFieldLayout = selectionFieldData?.layout ?? DEFUALT_SELECTION_FIELD_LAYOUT;
    const optionList = selectionFieldData?.options ?? [];
    const enableMultipleSelection = selectionFieldData?.enableMultipleSelection;
    const enableOther = selectionFieldData?.enableOther;
    const isHorizontal = selectionFieldLayout === SelectionFieldLayoutType.Horizontal;
    const stackDirection = isHorizontal ? 'row' : 'column';

    const optionStyle = useMemo(() => {
      return {
        color: color,
        fontFamily: fontFamily,
        fontSize: fontSize,
      };
    }, [fontFamily, color, fontSize]);

    const labelStyles = useMemo(() => {
      return {
        gap: gap,
        color: color,
        fontFamily: fontFamily,
        fontSize: fontSize,
        fontWeight: fontWeight,
        fontStyle: fontStyle,
        lineHeight: lineHeight,
        textAlign: textAlign,
        textDecoration: textDecoration,
      };
    }, [gap, color, fontFamily, fontSize, fontWeight, fontStyle, lineHeight, textAlign, textDecoration]);

    const otherInputBoxStyle = useMemo(
      () => ({
        backgroundColor,
        borderColor,
        borderRadius,
        borderWidth,
      }),
      [backgroundColor, borderColor, borderRadius, borderWidth],
    );

    const otherInputFontStyle = useMemo(
      () => ({
        color,
        fontFamily,
        fontSize,
        fontWeight,
        fontStyle,
        textDecoration,
      }),
      [color, fontFamily, fontSize, fontWeight, fontStyle, textDecoration],
    );

    const handleLabelTextClick = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setWidgetState(WidgetState.Edit);
    };

    const handleLabelClick = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setWidgetState(WidgetState.Active);
    };

    const handleInteractionSubmission = useCallback(
      (values: string[]) => {
        if (!isReadOnly || values === undefined) return;

        // Update value for the interaction submission
        const result = values.map((value) => value.replace(/^\d+-\s*/, '')).join(', ');
        const value = values.join(',');
        dispatch(
          updateForm({
            id: widgetId,
            fields: {
              label: formField.label,
              input: result,
              value: value,
            },
          }),
        );
      },
      [dispatch, isReadOnly, formField, widgetId],
    );

    const handleRadioChange = useCallback(
      (value: string) => {
        setWidgetState(WidgetState.Active);

        if (!isReadOnly) return;

        setValue([value]);

        handleInteractionSubmission([value]);
      },
      [isReadOnly, handleInteractionSubmission, setWidgetState],
    );

    const handleCheckboxChange = useCallback(
      (values: string[]) => {
        setWidgetState(WidgetState.Active);

        if (!isReadOnly) return;

        const sortedValue = values.sort();
        setValue(sortedValue);

        handleInteractionSubmission(sortedValue);
      },
      [isReadOnly, handleInteractionSubmission, setWidgetState],
    );

    const handleOtherInputSubmit = useCallback(() => {
      if (!isReadOnly) return;

      handleInteractionSubmission(value ?? []);
    }, [value, isReadOnly, handleInteractionSubmission]);

    const handleOtherInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (!isReadOnly) return;

        setOtherValue(e.target.value);

        const currentValue = value ?? [];
        const targetPrefix = selectionFieldData?.options.length + '-';
        const targetIndex = currentValue.findIndex((v) => v.startsWith(targetPrefix));
        const parsedValue = `${targetPrefix}${e.target.value}`;
        const isCheckboxGroup = selectionFieldData?.enableMultipleSelection;

        if (isCheckboxGroup) {
          if (targetIndex >= 0) {
            // Update the existing value
            const newValues = [...currentValue];
            newValues[targetIndex] = parsedValue;
            setValue(newValues);
          } else {
            // Push the new value
            setValue([...currentValue, parsedValue]);
          }
        } else {
          setValue([parsedValue]);
        }
      },
      [value, selectionFieldData, isReadOnly],
    );

    return (
      <InputBase
        id={`form-SelectionField-${widgetId}`}
        ref={ref}
        formType={FormType.SelectionField}
        label={formField.label}
        fontSize={fontSize}
        labelStyles={labelStyles}
        labelPosition={labelPosition}
        isLabelFocused={isWidgetSelected && isLabelFocused}
        isReadOnly={isReadOnly}
        onLabelClick={handleLabelClick}
        onLabelTextClick={handleLabelTextClick}
        containerProps={{
          pointerEvents: disableTabbability ? 'none' : 'auto',
        }}
      >
        {enableMultipleSelection ? (
          <CheckboxGroup
            onChange={handleCheckboxChange}
            value={!isReadOnly ? [''] : value}
            data-testid={SELECTION_FIELD_INPUT_TEST_ID}
          >
            <Stack direction={stackDirection} gap={isHorizontal ? 4 : 0}>
              {optionList.map((option) => (
                <Checkbox
                  key={option.index}
                  value={`${option.index}-${option.value}`}
                  sx={{
                    '.chakra-checkbox__control': {
                      backgroundColor: 'white',
                    },
                  }}
                  minH='6' // For empty labels
                >
                  <Text {...optionStyle}>{option.value}</Text>
                </Checkbox>
              ))}
              {enableOther && (
                <Flex justifyContent='flex-start' alignItems='center' gap={2}>
                  <Checkbox
                    value={`${optionList.length}-${otherValue}`}
                    sx={{
                      '.chakra-checkbox__control': {
                        backgroundColor: 'white',
                      },
                    }}
                    minH='6' // For empty labels
                  >
                    <Text {...optionStyle}>{t('settingsSideBar.question.addOtherLabel')}</Text>
                  </Checkbox>
                  <OtherInput
                    value={otherValue}
                    boxStyle={otherInputBoxStyle}
                    fontStyle={otherInputFontStyle}
                    onChange={handleOtherInputChange}
                    onSubmit={handleOtherInputSubmit}
                  />
                </Flex>
              )}
            </Stack>
          </CheckboxGroup>
        ) : (
          <RadioGroup
            onChange={handleRadioChange}
            value={!isReadOnly ? '' : value?.[0] ?? ''}
            data-testid={SELECTION_FIELD_INPUT_TEST_ID}
          >
            <Stack direction={stackDirection} gap={isHorizontal ? 4 : 0}>
              {optionList.map((option) => (
                <Radio
                  key={option.index}
                  value={`${option.index}-${option.value}`}
                  backgroundColor='white'
                  minH='6' // For empty labels
                >
                  <Text {...optionStyle}>{option.value}</Text>
                </Radio>
              ))}
              {enableOther && (
                <Flex justifyContent='flex-start' alignItems='center' gap={2}>
                  <Radio
                    value={`${optionList.length}-${otherValue}`}
                    backgroundColor='white'
                    minH='6' // For empty labels
                  >
                    <Text {...optionStyle}>{t('settingsSideBar.question.addOtherLabel')}</Text>
                  </Radio>
                  <OtherInput
                    value={otherValue}
                    boxStyle={otherInputBoxStyle}
                    fontStyle={otherInputFontStyle}
                    onChange={handleOtherInputChange}
                    onSubmit={handleOtherInputSubmit}
                  />
                </Flex>
              )}
            </Stack>
          </RadioGroup>
        )}
      </InputBase>
    );
  },
);

SelectionField.displayName = 'SelectionField';

export default SelectionField;

/**
 * Other Input field
 */
const OtherInput = memo(({ value, boxStyle = {}, fontStyle = {}, onChange, onSubmit }: OtherInputProps) => (
  <Input
    value={value}
    size='xs'
    w='full'
    _focus={{}}
    _hover={{}}
    onChange={onChange}
    onBlur={onSubmit}
    {...boxStyle}
    {...fontStyle}
  />
));

OtherInput.displayName = 'OtherInput';
